import Renderable from '../../../Component/Renderable';
import ElementComponent from '../../../Component/UI/Element';
import Game from '../../../Game';
import GameObject from '../../../GameObject/GameObject';
import { GameObjectEvent } from '../../../GameObject/GameObjectEvent';
import WorldHudIcon from '../../../Prototype/UI/WorldHudIcon';
import { captureException } from '../../../Sentry';
import { IGameState } from '../../../State/Game/IGameState';
import { UIActions } from '../../../State/UI/UIActions';
import { CrossPromoStatus } from '../../../State/User/UserActions';
import Point from '../../../Utility/Point';
import { CrossPromoData } from '../../../Utility/SDK/CrossPromo';
import { getLanguageString } from '../../../Utility/SDK/Language';
import { StartAnimation } from '../../Animation/AnimationHelper';
import BaseUIHandler from '../BaseUIHandler';
import BaseUISystem from '../BaseUISystem';
import WorldHUDHandler from './WorldHUDHandler';

export default class CrossPromoHUD extends BaseUIHandler {
    private btnXPromo: WorldHudIcon;

    constructor(uiSystem: BaseUISystem) {
        super('crossPromoHUD', uiSystem, false);
        this.alwaysVisible = true;
    }

    public handleStateChanged(state: IGameState): void {
        if (!this.btnXPromo || this.btnXPromo.destroyed) return;

        this.btnXPromo.getComponent(Renderable).visible =
            state.userState.crossPromo === CrossPromoStatus.ENABLED;
    }

    protected onCreate(): void {
        this.prepareCrossPromo().catch(e => captureException(e));
    }

    private async prepareCrossPromo() {
        const crossPromoData = await getLoadCrossPromoPromise();

        if (!crossPromoData || !this.active) return;

        const hud: WorldHUDHandler = this.system.handlers.find(
            handler => handler instanceof WorldHUDHandler
        ) as WorldHUDHandler;

        if (!hud) return;

        this.btnXPromo = GameObject.Instantiate(WorldHudIcon, {
            name: 'crossPromoIcon',
            key: 'xpromo_icon',
        });

        const container = hud.element.find('bottom_container');
        if (!container) return;
        container.addChild(this.btnXPromo);

        this.btnXPromo.on(GameObjectEvent.SELECTED, () => {
            this.system.state.dispatch(UIActions.showPopup('crossPromoMenu'));
        });

        const btnElement = this.btnXPromo.getComponent(ElementComponent);
        btnElement.offsetTopLeft = new Point(btnElement.offsetTopLeft.x, 170);
        btnElement.offsetBottomRight = new Point(
            btnElement.offsetBottomRight.x,
            170
        );

        btnElement.scale.set(0.5, 0.5);

        StartAnimation(this.btnXPromo, 'HUDIconIdle');
    }
}

/**
 * CrossPromo promise-loader reference.
 */
let loadCrossPromoPromise: Promise<CrossPromoData>;
export function getLoadCrossPromoPromise(): Promise<CrossPromoData> {
    if (!loadCrossPromoPromise) {
        loadCrossPromoPromise = loadCrossPromo();
    }
    return loadCrossPromoPromise;
}

async function loadCrossPromo(): Promise<CrossPromoData> {
    const crossPromoData = await Game.sdk.getCrossPromo();

    // Check enabled.
    if (!crossPromoData || !crossPromoData.xPromoEnabled) return;

    // Check language is supported.
    if (
        crossPromoData.xPromoData.targetLanguages.indexOf(
            Game.sdk.getLanguage()
        ) === -1
    )
        return;

    const languageKey = Game.sdk.getLanguage();

    const iconURL = getLanguageString(
        crossPromoData.xPromoData.iconURL,
        languageKey
    );
    const headerURL = getLanguageString(
        crossPromoData.xPromoData.headerURL,
        languageKey
    );

    if (!Game.resource.exists('xpromo_icon')) {
        Game.resource.add('xpromo_icon', iconURL);
    }

    if (!Game.resource.exists('xpromo_header')) {
        Game.resource.add('xpromo_header', headerURL);
    }

    await Promise.all([
        Game.resource.wait('xpromo_icon'),
        Game.resource.wait('xpromo_header'),
    ]);

    return crossPromoData;
}
